import React, { useState } from 'react';
import { faGoogle } from '@fortawesome/free-brands-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FaDesktop, FaMobileScreenButton } from 'react-icons/fa6';
import { getColor, isFirstRow, isLastRow } from '@/helpers/utils';
import { Badge, Button, Spinner, Table } from 'react-bootstrap';
import { AiOutlineZoomIn } from 'react-icons/ai';
import { useDispatch } from 'react-redux';
import { showCannibalizationsModal } from '@/app/KeywordTracker/reducers/cannibalizationsModal';
import { trackerTooltips } from '@/app/KeywordTracker/tooltip/trackerTooltips';
import { showconflictModal } from '@/app/KeywordTracker/reducers/conflictModal';
import { commonTooltips } from '@/app/common/tooltip/commonTooltips';
import { useAppContext } from '@/Main';
import { showDrawer } from '@/app/common/reducers/drawer';
import { difficulyRanges, serpDifficuly } from '@/helpers/serpDifficulity';
import {
  intentMap,
  purposeMap
} from '@/app/KeywordResearch/config/keywordGapColumnConfig';
import chroma from 'chroma-js';
import RankRenderer from '@/app/common/components/CellRenderers/RankRenderer';
import ChildrensTooltip from '@/components/common/ChildrensTooltip';
import RankChangeRenderer from '@/app/common/components/CellRenderers/RankChangeRenderer';
import RankChangeRendererNew from '@/app/common/components/CellRenderers/RankChangeRendererNew';
import SmallButton from '@/app/common/components/SmallButton';
import classNames from 'classnames';
import AiLogo from '@/assets/img/artificial-intelligence.svg?react';
import Divider from '@/components/common/Divider';
import RankVolumeChart from '@/app/common/components/CellRenderers/RankVolumeChart';
import timeStampToDate from '@/helpers/timeStampToDate';
import URLRenderer from '@/app/common/components/CellRenderers/URLRenderer';
import HelpTooltip from '@/app/common/components/HelpTooltip';
import SubtleBadge from '@/components/common/SubtleBadge';
import numberHumanizer from '@/helpers/numberHumanizer';

export const keywordsColumnConfig = (tags, groups, landings, locations) => [
  {
    field: 'title',
    tooltip: trackerTooltips.mainGrid.columns.title,
    type: 'string',
    title: 'کلمه کلیدی',
    width: 250,
    searchable: true,
    cellRenderer: params => {
      let keywordDecoded;
      const dispatch = useDispatch();
      try {
        keywordDecoded = decodeURIComponent(params.value);
      } catch (err) {
        keywordDecoded = params.value;
      }
      return (
        <div className="text-nowrap">
          <span
            style={{
              fontFeatureSettings: '"tnum" 0',
              fontVariantNumeric: 'normal',
              unicodeBidi: 'plaintext'
            }}
            className="mx-1"
          >
            {keywordDecoded}{' '}
          </span>
          {params.data.num_of_notes > 0 && (
            <span
              className="mx-1 position-relative cursor-pointer"
              onClick={() => {
                dispatch(
                  showDrawer({
                    key: 'keyword_notes',
                    payload: {
                      keyword_id: params.data.id,
                      keyword: params.data.title
                    }
                  })
                );
              }}
            >
              <FontAwesomeIcon icon={['far', 'comment']} />
              <span
                style={{
                  position: 'absolute',
                  top: '-3px',
                  left: '7px'
                }}
              >
                <div
                  className={`rounded-pill d-flex align-items-center justify-content-center badge badge-subtle-warning`}
                  style={{
                    width: 12,
                    height: 12,
                    padding: 0,
                    fontSize: 9
                  }}
                >
                  {params.data.num_of_notes < 100
                    ? params.data.num_of_notes
                    : '+۹۹'}
                </div>
              </span>
            </span>
          )}
          {params.data.device_type === 1 ? (
            <FaDesktop className="mx-1" title="دسکتاپ" />
          ) : (
            <FaMobileScreenButton className="mx-1" title="موبایل" />
          )}
          <span className="border text-nowrap border-400 border-1 fs--2 rounded-2 py-0 px-1">
            {params?.data?.location}
          </span>
        </div>
      );
    }
  },
  {
    field: 'rank',
    tooltip: trackerTooltips.mainGrid.columns.rank,
    type: 'number',
    title: 'رتبه فعلی',
    width: 120,
    searchable: true,
    gp: 'رتبه',
    cellRenderer: params =>
      params.value ? (
        <RankRenderer rank={params.value} />
      ) : (
        <>
          بررسی نشده{' '}
          <ChildrensTooltip
            showOnHover={false}
            placement="top"
            html={trackerTooltips.mainGrid.noChecked}
          >
            <FontAwesomeIcon
              className="cursor-pointer fs--2"
              icon="exclamation-circle"
            />
          </ChildrensTooltip>
        </>
      )
  },
  {
    gp: 'رتبه',
    field: 'ranks_trand',
    tooltip: trackerTooltips.mainGrid.columns.ranks_trand,
    sortable: false,
    type: 'array',
    title: 'روند رتبه',
    width: 120,
    cellStyle: {
      textAlign: 'center',
      margin: '0px'
    },
    cellClass: 'over-flow',
    cellRenderer: params => {
      return params?.value?.ranks && params?.value?.update_dates ? (
        <RankVolumeChart
          isFirstRow={isFirstRow(params)}
          isLastRow={isLastRow(params)}
          count={22}
          dates={params?.value?.update_dates}
          series={[
            {
              name: 'روند رتبه',
              data: params?.value?.ranks?.map((value, i) => ({
                name: params?.value?.update_dates[i],
                value: value,
                itemStyle: {
                  color: getColor('navbar-vertical-default-link-active-color')
                }
              }))
            }
          ]}
        />
      ) : (
        ''
      );
    }
  },
  {
    columnGroupShow: 'open',
    field: 'best_rank',
    tooltip: trackerTooltips.mainGrid.columns.best_rank,
    type: 'number',
    title: 'بهترین رتبه',
    width: 112,
    searchable: true,
    gp: 'رتبه',
    cellRenderer: params => <RankRenderer rank={params.value} />
  },
  {
    columnGroupShow: 'open',
    field: 'worst_rank',
    tooltip: trackerTooltips.mainGrid.columns.worst_rank,
    type: 'number',
    title: 'بدترین رتبه',
    width: 112,
    searchable: true,
    gp: 'رتبه',
    cellRenderer: params => <RankRenderer rank={params.value} />
  },
  {
    columnGroupShow: 'open',
    gp: 'رتبه',
    field: 'first_rank',
    tooltip: trackerTooltips.mainGrid.columns.first_rank,

    type: 'number',
    title: 'اولین رتبه',
    width: 100,
    searchable: true,
    cellRenderer: params =>
      params.value ? (
        <RankRenderer rank={params.value} />
      ) : (
        <>
          بررسی نشده{' '}
          <ChildrensTooltip
            showOnHover={false}
            placement="top"
            html={trackerTooltips.mainGrid.noChecked}
          >
            <FontAwesomeIcon
              className="cursor-pointer fs--2"
              icon="exclamation-circle"
            />
          </ChildrensTooltip>
        </>
      )
  },

  {
    columnGroupShow: 'open',
    gp: 'رتبه',
    field: 'previous_rank',
    type: 'number',
    title: 'رتبه قبلی',
    tooltip: trackerTooltips.mainGrid.columns.previous_rank,

    width: 100,
    searchable: true,
    cellRenderer: params => <RankRenderer rank={params.value} />
  },
  {
    columnGroupShow: 'open',

    gp: 'رتبه',
    field: 'average_rank',
    type: 'number',
    title: 'رتبه میانگین',
    width: 120,
    searchable: true,
    tooltip: trackerTooltips.mainGrid.columns.average_rank
  },
  {
    columnGroupShow: 'open',
    gp: 'رتبه',
    field: 'preiod_rank',
    tooltip: trackerTooltips.mainGrid.columns.preiod_rank,
    type: 'number',
    title: 'رتبه ابتدای بازه',
    width: 130,
    searchable: true,
    cellRenderer: params => <RankRenderer rank={params.value} />
  },

  {
    gp: 'تغییرات',
    field: 'change_rank',
    tooltip: trackerTooltips.mainGrid.columns.change_rank,

    type: 'number',
    title: 'تغییر رتبه',
    width: 100,
    searchable: true,
    cellRenderer: params => <RankChangeRendererNew rank={params.data.rank} previous_rank={params.data.previous_rank} change={params.value} />
  },
  {
    columnGroupShow: 'open',
    gp: 'تغییرات',
    field: 'change_average_rank',
    tooltip: trackerTooltips.mainGrid.columns.change_average_rank,
    type: 'number',
    title: 'تغییر میانگین',
    width: 135,
    searchable: true,
    cellRenderer: params => <RankChangeRenderer change={params.value} />
  },
  {
    columnGroupShow: 'open',
    gp: 'تغییرات',
    field: 'change_first_rank',
    tooltip: trackerTooltips.mainGrid.columns.change_first_rank,
    type: 'number',
    title: 'تغییر ابتدا',
    width: 120,
    searchable: true,
    cellRenderer: params => <RankChangeRenderer change={params.value} />
  },
  {
    columnGroupShow: 'open',
    gp: 'تغییرات',
    field: 'change_preiod_rank',
    tooltip: trackerTooltips.mainGrid.columns.change_preiod_rank,
    type: 'number',
    title: 'تغییر رتبه ابتدای بازه',
    width: 160,
    searchable: true,
    cellRenderer: params => <RankChangeRenderer change={params.value} />
  },
  {
    field: 'difficulty',
    gp: 'سنجه‌های کلمه',
    suppressHeaderMenuButton: true,
    type: 'number',
    title: 'سختی ',
    tooltip:
      'هرچه مقدار بالاتر باشد، کسب رتبه در بین ۱۰ نتیجه اول گوگل دشوارتر خواهد بود. سختی بیشتر یعنی نیاز به زمان، هزینه و تکنیک‌های پیشرفته‌تر برای رسیدن به رتبه‌های برتر.',
    width: 90,
    searchable: true,
    cellRenderer: params =>
      params.value && (
        <div className="d-flex align-items-center justify-content-center">
          <ChildrensTooltip
            placement="top"
            html={
              <div>
                سختی کلمه کلیدی
                <Divider />
                <Table borderless size="sm">
                  <tbody>
                    {difficulyRanges.map(([start, end]) => (
                      <tr
                        className={classNames({
                          'fw-black fs--1 position-relative':
                            serpDifficuly(start).color ===
                            serpDifficuly(params.value)?.color
                        })}
                        key={start}
                      >
                        <td className="d-flex  align-items-center gap-1">
                          {serpDifficuly(start).color ===
                            serpDifficuly(params.value)?.color && (
                            <FontAwesomeIcon
                              style={{
                                right: 3,
                                top: '50%',
                                transform: 'translateY(-50%)'
                              }}
                              icon="chevron-left"
                              className="position-absolute text-primary"
                              size="sm"
                            />
                          )}
                          <span
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              direction: 'ltr',
                              height: '20px',
                              width: '28px',
                              fontSize: '12px',
                              backgroundColor: serpDifficuly(start).color
                            }}
                            className="fs--2 px-2 py-0.5 text-white rounded-2"
                          >
                            {start}
                          </span>
                          <FontAwesomeIcon icon="minus" />
                          <span
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              direction: 'ltr',
                              height: '20px',
                              width: '28px',
                              fontSize: '12px',
                              backgroundColor: serpDifficuly(start).color
                            }}
                            className="fs--2 px-2 py-0.5 text-white rounded-2"
                          >
                            {end}
                          </span>
                        </td>
                        <td className="text-white">
                          {serpDifficuly(start).label}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            }
          >
            <div
              className="fs-1 rounded-2 w-full"
              style={{
                fontWeight: 'bold'
              }}
            >
              {/* {serpDifficuly(params.value).label}{' '} */}
              <span
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  direction: 'ltr',
                  marginTop: '6px',
                  height: '20px',
                  width: '28px',
                  fontSize: '12px',
                  backgroundColor: serpDifficuly(params.value).color
                }}
                className="fs--2 px-2 py-0.5 text-white rounded-2"
              >
                {params.value}
              </span>
            </div>
          </ChildrensTooltip>
        </div>
      )
  },
  {
    gp: 'سنجه‌های کلمه',
    field: 'search_volume_miztols',
    tooltip: trackerTooltips.mainGrid.columns.mizfa_tools_search_volume,
    type: 'number',
    title: 'میزان جستجو',
    width: 138,
    searchable: false,
    cellRenderer: params => {
      const [opened, setOpened] = useState(false);
      const dispatch = useDispatch();
      return !params.data.ads_last_check ? (
        <ChildrensTooltip
          showOnHover={false}
          placement="top"
          html={trackerTooltips.mainGrid.noAds}
        >
          <FontAwesomeIcon
            className="cursor-pointer fs--2"
            icon="exclamation-circle"
          />
        </ChildrensTooltip>
      ) : !params.value ? (
        <HelpTooltip html={commonTooltips.searchVolume} />
      ) : (
        <div className="d-flex align-items-center gap-1">
          <span
            onClick={() => {
              setOpened(true);
              setTimeout(() => {
                setOpened(false);
              }, 1500);
            }}
          >
            {!opened ? (
              <AiOutlineZoomIn
                onClick={() => {
                  dispatch(
                    showDrawer({
                      key: 'search_volume_tracker',
                      payload: {
                        location_code: params.data.location_code,
                        language_code: params.data.language_code,
                        keywords: [
                          {
                            keyword: params.data.title,
                            num_of_sv: params.value
                          }
                        ]
                      }
                    })
                  );
                }}
                icon="search-plus"
                className="cursor-pointer fs-0"
              />
            ) : (
              <Spinner
                animation="border"
                style={{
                  width: '16px',
                  marginBottom: '-3px',
                  paddingLeft: '3px',
                  height: '16px'
                }}
              />
            )}
          </span>
          {numberHumanizer(params.value)}
        </div>
      );
    }
  },
  {
    field: 'intent',
    gp: 'سنجه‌های کلمه',
    suppressHeaderMenuButton: true,
    type: 'select',
    icon: (
      <AiLogo
        style={{
          width: 13,
          marginTop: 6,
          fill: 'var(--ag-header-foreground-color)'
        }}
      />
    ),
    title: 'نیت',
    width: 90,
    searchable: true,
    cellRenderer: params => {
      return (
        <div className="w-100 d-flex h-100 align-items-center justify-content-start gap-2">
          {params.value.map((intent, i) => (
            <ChildrensTooltip
              placement="top"
              key={i}
              delayToRemove={0}
              html={intentMap[intent]?.tooltip}
            >
              <SubtleBadge
                pill
                key={i}
                bg={intentMap[intent]?.variant}
                className="d-flex align-items-center gap-1  fs--2"
              >
                {intent}
              </SubtleBadge>
            </ChildrensTooltip>
          ))}
        </div>
      );
    },
    tooltip: (
      <div className="text-start">
        نیت جستجو (Search Intent) یعنی قصد کاربر از جستجوی یک کلمه کلیدی چیه؟
        چهار نوع اصلی داره:
        <br /> Informational (هدف اطلاعاتی): کاربر به دنبال اطلاعات درباره یک
        موضوع خاص است. مثال: “چگونه یک ماشین دنده اتومات تعمیر کنیم؟” “فواید
        ورزش برای سلامتی.” این کلمات برای نوشتن مقالات مناسبن. <br />
        Navigational (هدف هدایت‌شده): کاربر به دنبال دسترسی به یک وب‌سایت یا
        موضوع خاص است. مثال: خرید عینک آفتابی در دیجیکالا.
        <br />
        Commercial (تحقیق تجاری): این نوع جستجو ترکیبیه و کاربر هنوز تصمیم
        نگرفته، اما در حال بررسی و مقایسه گزینه‌هاست. مثال: “بهترین گوشی‌های
        2025.” “مقایسه پژو 206 و تیبا.” <br />
        Transactional (هدف تراکنشی یا تجاری): کاربر قصد خرید یا انجام یک اقدام
        خاص را دارد. مثال: “خرید لپ‌تاپ اپل.” “رزرو هتل در کیش.” داده های ستون
        نیت از هوش مصنوعی گرفته میشه.
      </div>
    )
  },
  {
    field: 'purpose',
    gp: 'سنجه‌های کلمه',
    suppressHeaderMenuButton: true,
    type: 'select',
    width: 90,
    title: 'هدف',
    icon: (
      <AiLogo
        style={{
          width: 13,
          marginTop: 6,
          fill: 'var(--ag-header-foreground-color)'
        }}
      />
    ),
    searchable: true,
    cellRenderer: params => {
      return (
        <div className="w-100 d-flex h-100 align-items-center justify-content-start gap-2">
          {params.value.map((purpose, i) => (
            <SubtleBadge
              pill
              key={i}
              bg="info"
              className="d-flex align-items-center gap-1  fs--2"
            >
              {purposeMap[purpose]}
            </SubtleBadge>
          ))}
        </div>
      );
    },
    tooltip: (
      <>
        پیشنهاد میده که کلمه برای چه نوع محتوایی مناسب‌تره. مثلاً برای
        <strong>&nbsp;دسته‌بندی </strong>سایت، <strong>&nbsp;مقاله</strong>، یا
        <strong>&nbsp;برند</strong> مفیده. با استفاده از این فیلتر می‌تونید
        کلمات کلیدی رو بر اساس هدف خاص خودتون (مثل ایجاد محتوای آموزشی، تبلیغاتی
        یا برندینگ) انتخاب کنید. داده های این بخش از هوش مصنوعی گرفته میشه.
      </>
    )
  },
  // {
  //   searchable: true,
  //   field: 'seed',
  //   // tooltip: trackerTooltips.mainGrid.columns.seed,
  //   type: 'string',
  // width: 90,
  //   title: 'ریشه'
  // },
  {
    field: 'snippets',
    tooltip: trackerTooltips.mainGrid.columns.snippets,
    sortable: false,
    type: 'array',
    title: 'ویژگی نتایج',
    width: 180,
    searchable: false,
    cellRenderer: params => {
      const {
        config: { isDark }
      } = useAppContext();
      return (
        params.value && (
          <>
            <div
              style={{ gap: '0.4rem' }}
              className="d-flex align-items-center h-100"
            >
              {params.value.length > 0 ? (
                <>
                  {params.value?.map(snippet => (
                    <ChildrensTooltip
                      key={snippet}
                      delayToRemove={0}
                      placement="top"
                      html={
                        trackerTooltips.mainGrid.columns.snipptetsTooltip[
                          snippet
                        ]
                      }
                    >
                      <img
                        style={{
                          width: '1.2em',
                          height: '1.2em',
                          filter: isDark ? 'invert(100%)' : ''
                        }}
                        src={`https://static.mizfa.tools/img/icon/feature-serp/${snippet}.svg`}
                      />
                    </ChildrensTooltip>
                  ))}
                </>
              ) : (
                <ChildrensTooltip
                  showOnHover={false}
                  placement="top"
                  html={trackerTooltips.mainGrid.noSnippets}
                >
                  <FontAwesomeIcon
                    className="cursor-pointer fs--2"
                    icon="exclamation-circle"
                  />
                </ChildrensTooltip>
              )}
            </div>
          </>
        )
      );
    }
  },
  {
    field: 'actions',
    type: 'array',
    sortable: false,
    title: 'عملیات',
    width: 160,
    searchable: false,
    noFilter: true,
    cellRenderer: params => {
      const dispatch = useDispatch();
      return (
        <>
          <div className="d-flex h-100 align-items-center gap-2">
            <ChildrensTooltip
              showOnHover={false}
              delayToRemove={50}
              placement="top"
              html={trackerTooltips.mainGrid.lineProgress}
            >
              <SmallButton
                onClick={() => {
                  dispatch(
                    showDrawer({
                      key: 'keyword_progress',
                      payload: {
                        keyword_id: params.data.id,
                        keyword: params.data.title
                      }
                    })
                  );
                }}
              >
                <FontAwesomeIcon fontSize="11px" icon="chart-line" />
              </SmallButton>
            </ChildrensTooltip>

            <ChildrensTooltip
              showOnHover={false}
              delayToRemove={50}
              placement="top"
              html={trackerTooltips.mainGrid.googleResultHistory}
            >
              <SmallButton
                onClick={() => {
                  dispatch(
                    showDrawer({
                      key: 'google_result_history',
                      payload: {
                        keyword_id: params.data.id,
                        keyword: params.data.title
                      }
                    })
                  );
                }}
              >
                <FontAwesomeIcon fontSize="11px" icon={faGoogle} />
              </SmallButton>
            </ChildrensTooltip>
            <ChildrensTooltip
              showOnHover={false}
              delayToRemove={50}
              placement="top"
              html={trackerTooltips.mainGrid.ranksHistory}
            >
              <SmallButton
                onClick={() => {
                  dispatch(
                    showDrawer({
                      key: 'rank_history',
                      payload: {
                        keyword_id: params.data.id,
                        keyword: params.data.title
                      }
                    })
                  );
                }}
              >
                <FontAwesomeIcon fontSize="11px" icon="history" />
              </SmallButton>
            </ChildrensTooltip>
            <ChildrensTooltip
              showOnHover={false}
              delayToRemove={50}
              placement="top"
              html={trackerTooltips.mainGrid.relatedKeywords}
            >
              <SmallButton
                onClick={() => {
                  dispatch(
                    showDrawer({
                      key: 'related_keywords',
                      payload: {
                        location_code: params.data.location_code,
                        language_code: params.data.language_code,
                        keyword: params.data.title
                      }
                    })
                  );
                }}
              >
                <FontAwesomeIcon fontSize="11px" icon="search" />
              </SmallButton>
            </ChildrensTooltip>
            {/* <ChildrensTooltip
              showOnHover={false}
              delayToRemove={50}
              placement="top"
              html={trackerTooltips.mainGrid.addDescription}
            >
              <FontAwesomeIcon
                className="cursor-pointer little-icon"
                icon="pen"
              />
            </ChildrensTooltip> */}
          </div>
        </>
      );
    }
  },
  {
    searchable: true,
    field: 'found_url',
    tooltip: trackerTooltips.mainGrid.columns.found_url,
    gp: 'صفحات',
    type: 'string',
    title: 'صفحه پیدا شده',
    cellRenderer: params => {
      const dispatch = useDispatch();
      return (
        params.value && (
          <ChildrensTooltip
            placement="top"
            delayToRemove={0}
            showOnHover={false}
            html={
              <div className="d-flex flex-column justify-content-start align-items-start">
                <div className="text-start">عنوان سئو این سایت در گوگل:</div>
                <div className="text-start fw-bold">
                  {' '}
                  {params.data.seo_title}
                </div>
                {/* <div className="text-start">نشانی:</div>
                <div dir="ltr" className="text-end fw-bold">
                  {params.value.url}
                </div> */}
              </div>
            }
          >
            <div className="d-flex align-items-center gap-2">
              <URLRenderer
                url={params.value}
                addition={
                  <>
                    {params.data.have_cannibal && (
                      <svg
                        onClick={() => {
                          dispatch(
                            showCannibalizationsModal({
                              keyword: params.data.title,
                              data: params.data.cannibalizations
                            })
                          );
                        }}
                        height="0.78em"
                        width="0.78em"
                        fill={getColor('gray-1000')}
                        xmlns="http://www.w3.org/2000/svg"
                        version="1.1"
                        x="0px"
                        y="0px"
                        className="fs-0 cursor-pointer"
                        viewBox="0 0 24 24"
                        enableBackground="new 0 0 24 24"
                      >
                        <g>
                          <g>
                            <path d="M19.1,4.9C17.3,3.1,14.8,2,12,2C9.2,2,6.7,3.1,4.9,4.9S2,9.2,2,12s1.1,5.3,2.9,7.1S9.2,22,12,22    c2.8,0,5.3-1.1,7.1-2.9L12,12L19.1,4.9z M10.5,8C9.7,8,9,7.3,9,6.5C9,5.7,9.7,5,10.5,5S12,5.7,12,6.5C12,7.3,11.3,8,10.5,8z"></path>
                          </g>
                          <g>
                            <circle cx="20" cy="12" r="2"></circle>
                          </g>
                        </g>
                      </svg>
                    )}
                    {
                      params?.data?.landing_url &&
                        params.data.found_url &&
                        params.data.found_url !== params.data.landing_url && (
                          // <ChildrensTooltip
                          //   showOnHover={false}
                          //   placement="top"
                          //   html={trackerTooltips.mainGrid.noChecked}
                          // >
                          <FontAwesomeIcon
                            onClick={() => {
                              dispatch(
                                showconflictModal({
                                  keyword: params.data.title,
                                  data: [
                                    {
                                      landing_url: params.data.landing_url,
                                      found_url: params.data.found_url
                                    }
                                  ]
                                })
                              );
                            }}
                            className="cursor-pointer text-warning fs--2"
                            icon="exclamation-triangle"
                          />
                        )
                      // </ChildrensTooltip>
                    }
                  </>
                }
              />
            </div>
          </ChildrensTooltip>
        )
      );
    }
  },
  {
    searchable: true,
    field: 'have_cannibal',
    gp: 'صفحات',
    type: 'boolean',
    tooltip: trackerTooltips.mainGrid.columns.have_cannibal,
    title: 'همنوع خواری',
    width: 130,
    cellRenderer: params => {
      const dispatch = useDispatch();
      return (
        params.value && (
          <div className="d-flex h-100 align-items-center justify-content-center">
            <SmallButton
              onClick={() => {
                dispatch(
                  showCannibalizationsModal({
                    keyword: params.data.title,
                    data: params.data.cannibalizations
                  })
                );
              }}
            >
              <svg
                height="0.7em"
                width="0.7em"
                fill="var(--falcon-btn-falcon-primary-color)"
                xmlns="http://www.w3.org/2000/svg"
                version="1.1"
                x="0px"
                y="0px"
                viewBox="0 0 24 24"
                enableBackground="new 0 0 24 24"
              >
                <g>
                  <g>
                    <path d="M19.1,4.9C17.3,3.1,14.8,2,12,2C9.2,2,6.7,3.1,4.9,4.9S2,9.2,2,12s1.1,5.3,2.9,7.1S9.2,22,12,22    c2.8,0,5.3-1.1,7.1-2.9L12,12L19.1,4.9z M10.5,8C9.7,8,9,7.3,9,6.5C9,5.7,9.7,5,10.5,5S12,5.7,12,6.5C12,7.3,11.3,8,10.5,8z"></path>
                  </g>
                  <g>
                    <circle cx="20" cy="12" r="2"></circle>
                  </g>
                </g>
              </svg>
            </SmallButton>
          </div>
        )
      );
    }
  },
  {
    searchable: true,
    field: 'landing_url',
    tooltip: trackerTooltips.mainGrid.columns.landing_url,
    gp: 'صفحات',
    type: 'select',
    title: 'صفحه هدف',
    cellRenderer: params => <URLRenderer url={params.value} />
  },

  {
    gp: 'صفحات',
    searchable: true,
    field: 'have_conflict_with_landing_url',
    tooltip: trackerTooltips.mainGrid.columns.have_conflict_with_landing_url,
    type: 'boolean',
    title: 'مغایرت با صفحه هدف',
    cellRenderer: params => {
      const dispatch = useDispatch();
      return params.value !== null ? (
        params.value ? (
          <div className="d-flex h-100 align-items-center justify-content-center">
            <SmallButton
              onClick={() => {
                dispatch(
                  showconflictModal({
                    keyword: params.data.title,
                    data: [
                      {
                        landing_url: params.data.landing_url,
                        found_url: params.data.found_url
                      }
                    ]
                  })
                );
              }}
            >
              <FontAwesomeIcon
                fontSize="11px"
                className="cursor-pointer"
                icon="exclamation-triangle"
              />
            </SmallButton>
          </div>
        ) : (
          // <SubtleBadge bg="danger">دارد</SubtleBadge>
          ''
        )
      ) : (
        ''
      );
    }
  },

  {
    // NEW
    // cellEditor: 'agRichSelectCellEditor',
    // cellEditorParams: {
    //   // valueListMaxHeight: 160,
    //   // allowTyping: true,
    //   // searchType: 'matchAny',
    //   // filterList: true,
    //   // highlightMatch: true,
    //   // keyCreator: params => params.value.name,
    //   values: ['English', 'Spanish', 'French', 'Portuguese', '(other)']
    // },

    field: 'group_title',
    tooltip: trackerTooltips.mainGrid.columns.group_title,
    type: 'select',
    title: 'دسته',
    width: 120,
    searchable: true,
    cellRenderer: params =>
      params.value ? (
        <div className="w-100 d-flex h-100 align-items-center justify-content-start gap-2">
          <SubtleBadge
            pill
            bg="info"
            className="d-flex align-items-center gap-1  fs--2"
          >
            {params.value}
          </SubtleBadge>
        </div>
      ) : (
        <ChildrensTooltip
          showOnHover={false}
          placement="top"
          html={trackerTooltips.mainGrid.noGroups}
        >
          <FontAwesomeIcon
            className="cursor-pointer fs--2"
            icon="exclamation-circle"
          />
        </ChildrensTooltip>
      )
  },
  {
    field: 'tags',
    tooltip: trackerTooltips.mainGrid.columns.tags,
    type: 'select',
    title: 'برچسب',
    width: 150,
    searchable: true,
    // editable: true,
    // cellEditor: 'agRichSelectCellEditor',
    // cellEditorParams: {
    //   values: [...tags]?.map(tag => ({ id: tag.value, title: tag.label })),
    //   cellRenderer: params => {
    //     return Array.isArray(params.value) ? (
    //       <div className="w-100 d-flex h-100 align-items-center justify-content-start gap-2">
    //         {params.value.map((v, i) => (
    //           <SubtleBadge
    //             pill
    //             key={i}
    //             bg="info"
    //             className="d-flex align-items-center gap-1  fs--2"
    //           >
    //             {v?.title}
    //           </SubtleBadge>
    //         ))}
    //       </div>
    //     ) : (
    //       params.value && (
    //         <SubtleBadge
    //           pill
    //           bg="info"
    //           className="d-flex align-items-center gap-1  fs--2"
    //         >
    //           {params?.value?.title}
    //         </SubtleBadge>
    //       )
    //     );
    //   },
    //   multiSelect: true,
    //   searchType: 'matchAny',
    //   allowTyping: true,
    //   filterList: true,
    //   highlightMatch: true,
    //   suppressMultiSelectPillRenderer: true,
    //   valueListMaxHeight: 220
    // },
    cellRenderer: params => {
      return Array.isArray(params.value) && params.value.length > 0 ? (
        <div className="w-100 d-flex h-100 align-items-center justify-content-start gap-2">
          {params.value.map((v, i) => (
            <SubtleBadge
              pill
              key={i}
              bg="info"
              className="d-flex align-items-center gap-1  fs--2"
            >
              {v?.title}
            </SubtleBadge>
          ))}
        </div>
      ) : (
        <ChildrensTooltip
          showOnHover={false}
          placement="top"
          html={trackerTooltips.mainGrid.noTags}
        >
          <FontAwesomeIcon
            className="cursor-pointer fs--2"
            icon="exclamation-circle"
          />
        </ChildrensTooltip>
      );
    }
  },
  {
    searchable: true,
    field: 'inserted_at',
    tooltip: trackerTooltips.mainGrid.columns.inserted_at,
    type: 'date',
    gp: 'تاریخ',
    title: 'تاریخ درج',
    width: 150,
    cellRenderer: params => <>{timeStampToDate(params.value)?.slice(8)}</>
  },
  {
    searchable: true,
    field: 'updated_at',
    tooltip: trackerTooltips.mainGrid.columns.updated_at,
    type: 'date',
    gp: 'تاریخ',
    title: 'تاریخ به‌روزرسانی',
    width: 150,
    cellRenderer: params => <>{timeStampToDate(params.value)?.slice(8)}</>
  },
  {
    field: 'result_count',
    tooltip: trackerTooltips.mainGrid.columns.result_count,
    type: 'number',
    title: 'تعداد نتایج',
    width: 160,
    searchable: true,
    cellRenderer: params =>
      params.data.device_type !== 1 ? (
        <ChildrensTooltip
          showOnHover={false}
          placement="top"
          html={trackerTooltips.mainGrid.noResOnPhones}
        >
          <FontAwesomeIcon
            className="cursor-pointer fs--2"
            icon="exclamation-circle"
          />
        </ChildrensTooltip>
      ) : (
        numberHumanizer(params.value)
      )
  },
  {
    gp: 'Google Ads',
    field: 'ads_search_volume',
    tooltip: trackerTooltips.mainGrid.columns.ads_avg_search_volume,
    type: 'number',
    title: 'SV',
    width: 100,
    searchable: true,
    cellRenderer: params =>
      !params.data.ads_last_check ? (
        <ChildrensTooltip
          showOnHover={false}
          placement="top"
          html={trackerTooltips.mainGrid.noAds}
        >
          <FontAwesomeIcon
            className="cursor-pointer fs--2"
            icon="exclamation-circle"
          />
        </ChildrensTooltip>
      ) : (
        numberHumanizer(params.value)
      )
  },
  {
    gp: 'Google Ads',
    field: 'ads_competition_index',
    tooltip: trackerTooltips.mainGrid.columns.ads_competition,
    type: 'number',
    title: 'Comp',
    width: 100,
    searchable: true,
    cellRenderer: params =>
      !params.data.ads_last_check ? (
        <ChildrensTooltip
          showOnHover={false}
          placement="top"
          html={trackerTooltips.mainGrid.noAds}
        >
          <FontAwesomeIcon
            className="cursor-pointer fs--2"
            icon="exclamation-circle"
          />
        </ChildrensTooltip>
      ) : (
        numberHumanizer(params.value)
      )
  },

  {
    field: 'length',
    tooltip: trackerTooltips.mainGrid.columns.length,
    type: 'number',
    title: 'طول',
    width: 160,
    searchable: true
  },
  {
    field: 'device_type',
    type: 'select',
    title: 'نوع دستگاه',
    width: 120,
    searchable: true,
    cellRenderer: params => (params.value === 1 ? 'رایانه' : 'گوشی')
  },
  {
    field: 'location',
    type: 'select',
    title: 'مکان جستجو',
    width: 120,
    searchable: true
  }
];

const DynamicInlineSVG = ({ snippet, color }) => {
  const [svgContent, setSvgContent] = useState(null);

  useEffect(() => {
    const fetchSVG = async () => {
      try {
        const response = await fetch(
          `https://static.mizfa.tools/img/icon/feature-serp/${snippet}.svg`
        );
        const svgText = await response.text();

        const parser = new DOMParser();
        const svgElement = parser
          .parseFromString(svgText, 'image/svg+xml')
          .querySelector('svg');

        if (svgElement) {
          // Apply the dynamic color to all paths in the SVG
          svgElement.querySelectorAll('path')?.forEach(path => {
            path.setAttribute('fill', color); // Apply dynamic color
          });

          // Set the modified SVG as a string
          setSvgContent(svgElement.outerHTML);
        }
      } catch (error) {
        console.error('Error fetching SVG:', error);
      }
    };

    fetchSVG();
  }, [snippet, color]);

  return (
    <SubtleBadge
      key={snippet}
      bg="s"
      className="d-flex rounded-2 align-items-center gap-1"
    >
      {/* Render the inline SVG content */}
      {svgContent && (
        <span
          dangerouslySetInnerHTML={{ __html: svgContent }}
          style={{ width: '1.4em', height: '1.4em' }}
        />
      )}
    </SubtleBadge>
  );
};

export const keywordsColumnConfigSec = [
  {
    field: 'title',
    tooltip: trackerTooltips.mainGrid.columns.title,
    type: 'string',
    title: 'کلمه کلیدی',
    width: 250,
    searchable: true,
    cellRenderer: params => {
      let keywordDecoded;
      try {
        keywordDecoded = decodeURIComponent(params.value);
      } catch (err) {
        keywordDecoded = params.value;
      }
      return (
        <div className="text-nowrap">
          <span
            style={{
              fontFeatureSettings: '"tnum" 0',
              fontVariantNumeric: 'normal',
              unicodeBidi: 'plaintext'
            }}
            className="mx-1"
          >
            {keywordDecoded}{' '}
          </span>
          {params.data.device_type === 1 ? (
            <FaDesktop className="mx-1" title="دسکتاپ" />
          ) : (
            <FaMobileScreenButton className="mx-1" title="موبایل" />
          )}
          <span className="border text-nowrap border-400 border-1 fs--2 rounded-2 py-0 px-1">
            {params?.data?.location}
          </span>
        </div>
      );
    }
  },
  {
    field: 'rank',
    tooltip: trackerTooltips.mainGrid.columns.rank,
    type: 'number',
    title: 'رتبه فعلی',
    width: 120,
    searchable: true,
    gp: 'رتبه',
    cellRenderer: params =>
      params.value ? (
        <RankRenderer rank={params.value} />
      ) : (
        <>
          بررسی نشده{' '}
          <ChildrensTooltip
            showOnHover={false}
            placement="top"
            html={trackerTooltips.mainGrid.noChecked}
          >
            <FontAwesomeIcon
              className="cursor-pointer fs--2"
              icon="exclamation-circle"
            />
          </ChildrensTooltip>
        </>
      )
  },
  {
    gp: 'رتبه',
    field: 'ranks_trand',
    tooltip: trackerTooltips.mainGrid.columns.ranks_trand,
    sortable: false,
    type: 'array',
    title: 'روند رتبه',
    width: 120,
    cellStyle: {
      textAlign: 'center',
      margin: '0px'
    },
    cellClass: 'over-flow',
    cellRenderer: params => {
      return params?.value?.ranks && params?.value?.update_dates ? (
        <RankVolumeChart
          isFirstRow={isFirstRow(params)}
          isLastRow={isLastRow(params)}
          count={22}
          dates={params?.value?.update_dates}
          series={[
            {
              name: 'روند رتبه',
              data: params?.value?.ranks?.map((value, i) => ({
                name: params?.value?.update_dates[i],
                value: value,
                itemStyle: {
                  color: getColor('navbar-vertical-default-link-active-color')
                }
              }))
            }
          ]}
        />
      ) : (
        ''
      );
    }
  },
  {
    columnGroupShow: 'open',
    field: 'best_rank',
    tooltip: trackerTooltips.mainGrid.columns.best_rank,
    type: 'number',
    title: 'بهترین رتبه',
    width: 112,
    searchable: true,
    gp: 'رتبه',
    cellRenderer: params => <RankRenderer rank={params.value} />
  },
  {
    columnGroupShow: 'open',
    field: 'worst_rank',
    tooltip: trackerTooltips.mainGrid.columns.worst_rank,
    type: 'number',
    title: 'بدترین رتبه',
    width: 112,
    searchable: true,
    gp: 'رتبه',
    cellRenderer: params => <RankRenderer rank={params.value} />
  },
  {
    columnGroupShow: 'open',
    gp: 'رتبه',
    field: 'first_rank',
    tooltip: trackerTooltips.mainGrid.columns.first_rank,

    type: 'number',
    title: 'اولین رتبه',
    width: 100,
    searchable: true,
    cellRenderer: params =>
      params.value ? (
        <RankRenderer rank={params.value} />
      ) : (
        <>
          بررسی نشده{' '}
          <ChildrensTooltip
            showOnHover={false}
            placement="top"
            html={trackerTooltips.mainGrid.noChecked}
          >
            <FontAwesomeIcon
              className="cursor-pointer fs--2"
              icon="exclamation-circle"
            />
          </ChildrensTooltip>
        </>
      )
  },

  {
    columnGroupShow: 'open',
    gp: 'رتبه',
    field: 'previous_rank',
    type: 'number',
    title: 'رتبه قبلی',
    tooltip: trackerTooltips.mainGrid.columns.previous_rank,

    width: 100,
    searchable: true,
    cellRenderer: params => <RankRenderer rank={params.value} />
  },
  {
    columnGroupShow: 'open',

    gp: 'رتبه',
    field: 'average_rank',
    type: 'number',
    title: 'رتبه میانگین',
    width: 120,
    searchable: true,
    tooltip: trackerTooltips.mainGrid.columns.average_rank
  },
  {
    columnGroupShow: 'open',
    gp: 'رتبه',
    field: 'preiod_rank',
    tooltip: trackerTooltips.mainGrid.columns.preiod_rank,
    type: 'number',
    title: 'رتبه ابتدای بازه',
    width: 130,
    searchable: true,
    cellRenderer: params => <RankRenderer rank={params.value} />
  },

  {
    gp: 'تغییرات',
    field: 'change_rank',
    tooltip: trackerTooltips.mainGrid.columns.change_rank,

    type: 'number',
    title: 'تغییر رتبه',
    width: 100,
    searchable: true,
    cellRenderer: params => <RankChangeRendererNew rank={params.data.rank} previous_rank={params.data.previous_rank} change={params.value} />
  },
  {
    columnGroupShow: 'open',
    gp: 'تغییرات',
    field: 'change_average_rank',
    tooltip: trackerTooltips.mainGrid.columns.change_average_rank,
    type: 'number',
    title: 'تغییر میانگین',
    width: 135,
    searchable: true,
    cellRenderer: params => <RankChangeRenderer change={params.value} />
  },
  {
    columnGroupShow: 'open',
    gp: 'تغییرات',
    field: 'change_first_rank',
    tooltip: trackerTooltips.mainGrid.columns.change_first_rank,
    type: 'number',
    title: 'تغییر ابتدا',
    width: 120,
    searchable: true,
    cellRenderer: params => <RankChangeRenderer change={params.value} />
  },
  {
    columnGroupShow: 'open',
    gp: 'تغییرات',
    field: 'change_preiod_rank',
    tooltip: trackerTooltips.mainGrid.columns.change_preiod_rank,
    type: 'number',
    title: 'تغییر رتبه ابتدای بازه',
    width: 160,
    searchable: true,
    cellRenderer: params => <RankChangeRenderer change={params.value} />
  },
  {
    field: 'difficulty',
    gp: 'سنجه‌های کلمه',
    suppressHeaderMenuButton: true,
    type: 'number',
    title: 'سختی ',
    tooltip:
      'هرچه مقدار بالاتر باشد، کسب رتبه در بین ۱۰ نتیجه اول گوگل دشوارتر خواهد بود. سختی بیشتر یعنی نیاز به زمان، هزینه و تکنیک‌های پیشرفته‌تر برای رسیدن به رتبه‌های برتر.',
    width: 90,
    searchable: true,
    cellRenderer: params =>
      params.value && (
        <div className="d-flex align-items-center justify-content-center">
          <ChildrensTooltip
            placement="top"
            html={
              <div>
                سختی کلمه کلیدی
                <Divider />
                <Table borderless size="sm">
                  <tbody>
                    {difficulyRanges.map(([start, end]) => (
                      <tr
                        className={classNames({
                          'fw-black fs--1 position-relative':
                            serpDifficuly(start).color ===
                            serpDifficuly(params.value)?.color
                        })}
                        key={start}
                      >
                        <td className="d-flex  align-items-center gap-1">
                          {serpDifficuly(start).color ===
                            serpDifficuly(params.value)?.color && (
                            <FontAwesomeIcon
                              style={{
                                right: 3,
                                top: '50%',
                                transform: 'translateY(-50%)'
                              }}
                              icon="chevron-left"
                              className="position-absolute text-primary"
                              size="sm"
                            />
                          )}
                          <span
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              direction: 'ltr',
                              height: '20px',
                              width: '28px',
                              fontSize: '12px',
                              backgroundColor: serpDifficuly(start).color
                            }}
                            className="fs--2 px-2 py-0.5 text-white rounded-2"
                          >
                            {start}
                          </span>
                          <FontAwesomeIcon icon="minus" />
                          <span
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              direction: 'ltr',
                              height: '20px',
                              width: '28px',
                              fontSize: '12px',
                              backgroundColor: serpDifficuly(start).color
                            }}
                            className="fs--2 px-2 py-0.5 text-white rounded-2"
                          >
                            {end}
                          </span>
                        </td>
                        <td className="text-white">
                          {serpDifficuly(start).label}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            }
          >
            <div
              className="fs-1 rounded-2 w-full"
              style={{
                fontWeight: 'bold'
              }}
            >
              {/* {serpDifficuly(params.value).label}{' '} */}
              <span
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  direction: 'ltr',
                  marginTop: '6px',
                  height: '20px',
                  width: '28px',
                  fontSize: '12px',
                  backgroundColor: serpDifficuly(params.value).color
                }}
                className="fs--2 px-2 py-0.5 text-white rounded-2"
              >
                {params.value}
              </span>
            </div>
          </ChildrensTooltip>
        </div>
      )
  },
  {
    gp: 'سنجه‌های کلمه',
    field: 'search_volume_miztols',
    tooltip: trackerTooltips.mainGrid.columns.mizfa_tools_search_volume,
    type: 'number',
    title: 'میزان جستجو',
    width: 138,
    searchable: false,
    cellRenderer: params => {
      const [opened, setOpened] = useState(false);
      const dispatch = useDispatch();
      return !params.data.ads_last_check ? (
        <ChildrensTooltip
          showOnHover={false}
          placement="top"
          html={trackerTooltips.mainGrid.noAds}
        >
          <FontAwesomeIcon
            className="cursor-pointer fs--2"
            icon="exclamation-circle"
          />
        </ChildrensTooltip>
      ) : !params.value ? (
        <HelpTooltip html={commonTooltips.searchVolume} />
      ) : (
        <div className="d-flex align-items-center gap-1">
          <span
            onClick={() => {
              setOpened(true);
              setTimeout(() => {
                setOpened(false);
              }, 1500);
            }}
          >
            {!opened ? (
              <AiOutlineZoomIn
                onClick={() => {
                  dispatch(
                    showDrawer({
                      key: 'search_volume_tracker',
                      payload: {
                        location_code: params.data.location_code,
                        language_code: params.data.language_code,
                        keywords: [
                          {
                            keyword: params.data.title,
                            num_of_sv: params.value
                          }
                        ]
                      }
                    })
                  );
                }}
                icon="search-plus"
                className="cursor-pointer fs-0"
              />
            ) : (
              <Spinner
                animation="border"
                style={{
                  width: '16px',
                  marginBottom: '-3px',
                  paddingLeft: '3px',
                  height: '16px'
                }}
              />
            )}
          </span>
          {numberHumanizer(params.value)}
        </div>
      );
    }
  },
  {
    field: 'intent',
    gp: 'سنجه‌های کلمه',
    suppressHeaderMenuButton: true,
    type: 'select',
    icon: (
      <AiLogo
        style={{
          width: 13,
          marginTop: 6,
          fill: 'var(--ag-header-foreground-color)'
        }}
      />
    ),
    title: 'نیت',
    width: 90,
    searchable: true,
    cellRenderer: params => {
      return (
        <div className="w-100 d-flex h-100 align-items-center justify-content-start gap-2">
          {params.value.map((intent, i) => (
            <ChildrensTooltip
              placement="top"
              key={i}
              delayToRemove={0}
              html={intentMap[intent]?.tooltip}
            >
              <SubtleBadge
                pill
                key={i}
                bg={intentMap[intent]?.variant}
                className="d-flex align-items-center gap-1  fs--2"
              >
                {intent}
              </SubtleBadge>
            </ChildrensTooltip>
          ))}
        </div>
      );
    },
    tooltip: (
      <div className="text-start">
        نیت جستجو (Search Intent) یعنی قصد کاربر از جستجوی یک کلمه کلیدی چیه؟
        چهار نوع اصلی داره:
        <br /> Informational (هدف اطلاعاتی): کاربر به دنبال اطلاعات درباره یک
        موضوع خاص است. مثال: “چگونه یک ماشین دنده اتومات تعمیر کنیم؟” “فواید
        ورزش برای سلامتی.” این کلمات برای نوشتن مقالات مناسبن. <br />
        Navigational (هدف هدایت‌شده): کاربر به دنبال دسترسی به یک وب‌سایت یا
        موضوع خاص است. مثال: خرید عینک آفتابی در دیجیکالا.
        <br />
        Commercial (تحقیق تجاری): این نوع جستجو ترکیبیه و کاربر هنوز تصمیم
        نگرفته، اما در حال بررسی و مقایسه گزینه‌هاست. مثال: “بهترین گوشی‌های
        2025.” “مقایسه پژو 206 و تیبا.” <br />
        Transactional (هدف تراکنشی یا تجاری): کاربر قصد خرید یا انجام یک اقدام
        خاص را دارد. مثال: “خرید لپ‌تاپ اپل.” “رزرو هتل در کیش.” داده های ستون
        نیت از هوش مصنوعی گرفته میشه.
      </div>
    )
  },
  {
    field: 'purpose',
    gp: 'سنجه‌های کلمه',
    suppressHeaderMenuButton: true,
    type: 'select',
    width: 90,
    title: 'هدف',
    icon: (
      <AiLogo
        style={{
          width: 13,
          marginTop: 6,
          fill: 'var(--ag-header-foreground-color)'
        }}
      />
    ),
    searchable: true,
    cellRenderer: params => {
      return (
        <div className="w-100 d-flex h-100 align-items-center justify-content-start gap-2">
          {params.value.map((purpose, i) => (
            <SubtleBadge
              pill
              key={i}
              bg="info"
              className="d-flex align-items-center gap-1  fs--2"
            >
              {purposeMap[purpose]}
            </SubtleBadge>
          ))}
        </div>
      );
    },
    tooltip: (
      <>
        پیشنهاد میده که کلمه برای چه نوع محتوایی مناسب‌تره. مثلاً برای
        <strong>&nbsp;دسته‌بندی </strong>سایت، <strong>&nbsp;مقاله</strong>، یا
        <strong>&nbsp;برند</strong> مفیده. با استفاده از این فیلتر می‌تونید
        کلمات کلیدی رو بر اساس هدف خاص خودتون (مثل ایجاد محتوای آموزشی، تبلیغاتی
        یا برندینگ) انتخاب کنید. داده های این بخش از هوش مصنوعی گرفته میشه.
      </>
    )
  },
  // {
  //   searchable: true,
  //   field: 'seed',
  //   // tooltip: trackerTooltips.mainGrid.columns.seed,
  //   type: 'string',
  // width: 90,
  //   title: 'ریشه'
  // },
  {
    field: 'snippets',
    tooltip: trackerTooltips.mainGrid.columns.snippets,
    sortable: false,
    type: 'array',
    title: 'ویژگی نتایج',
    width: 180,
    searchable: false,
    cellRenderer: params => {
      const {
        config: { isDark }
      } = useAppContext();
      return (
        params.value && (
          <>
            <div
              style={{ gap: '0.4rem' }}
              className="d-flex align-items-center h-100"
            >
              {params.value.length > 0 ? (
                <>
                  {params.value?.map(snippet => (
                    <ChildrensTooltip
                      key={snippet}
                      delayToRemove={0}
                      placement="top"
                      html={
                        trackerTooltips.mainGrid.columns.snipptetsTooltip[
                          snippet
                        ]
                      }
                    >
                      <img
                        style={{
                          width: '1.2em',
                          height: '1.2em',
                          filter: isDark ? 'invert(100%)' : ''
                        }}
                        src={`https://static.mizfa.tools/img/icon/feature-serp/${snippet}.svg`}
                      />
                    </ChildrensTooltip>
                  ))}
                </>
              ) : (
                <ChildrensTooltip
                  showOnHover={false}
                  placement="top"
                  html={trackerTooltips.mainGrid.noSnippets}
                >
                  <FontAwesomeIcon
                    className="cursor-pointer fs--2"
                    icon="exclamation-circle"
                  />
                </ChildrensTooltip>
              )}
            </div>
          </>
        )
      );
    }
  },
  {
    field: 'actions',
    type: 'array',
    sortable: false,
    title: 'عملیات',
    width: 160,
    searchable: false,
    noFilter: true,
    cellRenderer: params => {
      const dispatch = useDispatch();
      return (
        <>
          <div className="d-flex h-100 align-items-center gap-2">
            <ChildrensTooltip
              showOnHover={false}
              delayToRemove={50}
              placement="top"
              html={trackerTooltips.mainGrid.lineProgress}
            >
              <SmallButton
                onClick={() => {
                  dispatch(
                    showDrawer({
                      key: 'keyword_progress',
                      payload: {
                        keyword_id: params.data.id,
                        keyword: params.data.title
                      }
                    })
                  );
                }}
              >
                <FontAwesomeIcon fontSize="11px" icon="chart-line" />
              </SmallButton>
            </ChildrensTooltip>

            <ChildrensTooltip
              showOnHover={false}
              delayToRemove={50}
              placement="top"
              html={trackerTooltips.mainGrid.googleResultHistory}
            >
              <SmallButton
                onClick={() => {
                  dispatch(
                    showDrawer({
                      key: 'google_result_history',
                      payload: {
                        keyword_id: params.data.id,
                        keyword: params.data.title
                      }
                    })
                  );
                }}
              >
                <FontAwesomeIcon fontSize="11px" icon={faGoogle} />
              </SmallButton>
            </ChildrensTooltip>
            <ChildrensTooltip
              showOnHover={false}
              delayToRemove={50}
              placement="top"
              html={trackerTooltips.mainGrid.ranksHistory}
            >
              <SmallButton
                onClick={() => {
                  dispatch(
                    showDrawer({
                      key: 'rank_history',
                      payload: {
                        keyword_id: params.data.id,
                        keyword: params.data.title
                      }
                    })
                  );
                }}
              >
                <FontAwesomeIcon fontSize="11px" icon="history" />
              </SmallButton>
            </ChildrensTooltip>
            <ChildrensTooltip
              showOnHover={false}
              delayToRemove={50}
              placement="top"
              html={trackerTooltips.mainGrid.relatedKeywords}
            >
              <SmallButton
                onClick={() => {
                  dispatch(
                    showDrawer({
                      key: 'related_keywords',
                      payload: {
                        location_code: params.data.location_code,
                        language_code: params.data.language_code,
                        keyword: params.data.title
                      }
                    })
                  );
                }}
              >
                <FontAwesomeIcon fontSize="11px" icon="search" />
              </SmallButton>
            </ChildrensTooltip>
            {/* <ChildrensTooltip
              showOnHover={false}
              delayToRemove={50}
              placement="top"
              html={trackerTooltips.mainGrid.addDescription}
            >
              <FontAwesomeIcon
                className="cursor-pointer little-icon"
                icon="pen"
              />
            </ChildrensTooltip> */}
          </div>
        </>
      );
    }
  },
  {
    searchable: true,
    field: 'found_url',
    tooltip: trackerTooltips.mainGrid.columns.found_url,
    gp: 'صفحات',
    type: 'string',
    title: 'صفحه پیدا شده',
    cellRenderer: params => {
      const dispatch = useDispatch();
      return (
        params.value && (
          <ChildrensTooltip
            placement="top"
            delayToRemove={0}
            showOnHover={false}
            html={
              <div className="d-flex flex-column justify-content-start align-items-start">
                <div className="text-start">عنوان سئو این سایت در گوگل:</div>
                <div className="text-start fw-bold">
                  {' '}
                  {params.data.seo_title}
                </div>
                {/* <div className="text-start">نشانی:</div>
                <div dir="ltr" className="text-end fw-bold">
                  {params.value.url}
                </div> */}
              </div>
            }
          >
            <div className="d-flex align-items-center gap-2">
              <URLRenderer
                url={params.value}
                addition={
                  <>
                    {params.data.have_cannibal && (
                      <svg
                        onClick={() => {
                          dispatch(
                            showCannibalizationsModal({
                              keyword: params.data.title,
                              data: params.data.cannibalizations
                            })
                          );
                        }}
                        height="0.78em"
                        width="0.78em"
                        fill={getColor('gray-1000')}
                        xmlns="http://www.w3.org/2000/svg"
                        version="1.1"
                        x="0px"
                        y="0px"
                        className="fs-0 cursor-pointer"
                        viewBox="0 0 24 24"
                        enableBackground="new 0 0 24 24"
                      >
                        <g>
                          <g>
                            <path d="M19.1,4.9C17.3,3.1,14.8,2,12,2C9.2,2,6.7,3.1,4.9,4.9S2,9.2,2,12s1.1,5.3,2.9,7.1S9.2,22,12,22    c2.8,0,5.3-1.1,7.1-2.9L12,12L19.1,4.9z M10.5,8C9.7,8,9,7.3,9,6.5C9,5.7,9.7,5,10.5,5S12,5.7,12,6.5C12,7.3,11.3,8,10.5,8z"></path>
                          </g>
                          <g>
                            <circle cx="20" cy="12" r="2"></circle>
                          </g>
                        </g>
                      </svg>
                    )}
                    {
                      params?.data?.landing_url &&
                        params.data.found_url &&
                        params.data.found_url !== params.data.landing_url && (
                          // <ChildrensTooltip
                          //   showOnHover={false}
                          //   placement="top"
                          //   html={trackerTooltips.mainGrid.noChecked}
                          // >
                          <FontAwesomeIcon
                            onClick={() => {
                              dispatch(
                                showconflictModal({
                                  keyword: params.data.title,
                                  data: [
                                    {
                                      landing_url: params.data.landing_url,
                                      found_url: params.data.found_url
                                    }
                                  ]
                                })
                              );
                            }}
                            className="cursor-pointer text-warning fs--2"
                            icon="exclamation-triangle"
                          />
                        )
                      // </ChildrensTooltip>
                    }
                  </>
                }
              />
            </div>
          </ChildrensTooltip>
        )
      );
    }
  },
  {
    searchable: true,
    field: 'have_cannibal',
    gp: 'صفحات',
    type: 'boolean',
    tooltip: trackerTooltips.mainGrid.columns.have_cannibal,
    title: 'همنوع خواری',
    width: 130,
    cellRenderer: params => {
      const dispatch = useDispatch();
      return (
        params.value && (
          <div className="d-flex h-100 align-items-center justify-content-center">
            <SmallButton
              onClick={() => {
                dispatch(
                  showCannibalizationsModal({
                    keyword: params.data.title,
                    data: params.data.cannibalizations
                  })
                );
              }}
            >
              <svg
                height="0.7em"
                width="0.7em"
                fill="var(--falcon-btn-falcon-primary-color)"
                xmlns="http://www.w3.org/2000/svg"
                version="1.1"
                x="0px"
                y="0px"
                viewBox="0 0 24 24"
                enableBackground="new 0 0 24 24"
              >
                <g>
                  <g>
                    <path d="M19.1,4.9C17.3,3.1,14.8,2,12,2C9.2,2,6.7,3.1,4.9,4.9S2,9.2,2,12s1.1,5.3,2.9,7.1S9.2,22,12,22    c2.8,0,5.3-1.1,7.1-2.9L12,12L19.1,4.9z M10.5,8C9.7,8,9,7.3,9,6.5C9,5.7,9.7,5,10.5,5S12,5.7,12,6.5C12,7.3,11.3,8,10.5,8z"></path>
                  </g>
                  <g>
                    <circle cx="20" cy="12" r="2"></circle>
                  </g>
                </g>
              </svg>
            </SmallButton>
          </div>
        )
      );
    }
  },
  {
    searchable: true,
    field: 'landing_url',
    tooltip: trackerTooltips.mainGrid.columns.landing_url,
    gp: 'صفحات',
    type: 'select',
    title: 'صفحه هدف',
    cellRenderer: params => <URLRenderer url={params.value} />
  },

  {
    gp: 'صفحات',
    searchable: true,
    field: 'have_conflict_with_landing_url',
    tooltip: trackerTooltips.mainGrid.columns.have_conflict_with_landing_url,
    type: 'boolean',
    title: 'مغایرت با صفحه هدف',
    cellRenderer: params => {
      const dispatch = useDispatch();
      return params.value !== null ? (
        params.value ? (
          <div className="d-flex h-100 align-items-center justify-content-center">
            <SmallButton
              onClick={() => {
                dispatch(
                  showconflictModal({
                    keyword: params.data.title,
                    data: [
                      {
                        landing_url: params.data.landing_url,
                        found_url: params.data.found_url
                      }
                    ]
                  })
                );
              }}
            >
              <FontAwesomeIcon
                fontSize="11px"
                className="cursor-pointer"
                icon="exclamation-triangle"
              />
            </SmallButton>
          </div>
        ) : (
          // <SubtleBadge bg="danger">دارد</SubtleBadge>
          ''
        )
      ) : (
        ''
      );
    }
  },

  {
    // NEW
    // cellEditor: 'agRichSelectCellEditor',
    // cellEditorParams: {
    //   // valueListMaxHeight: 160,
    //   // allowTyping: true,
    //   // searchType: 'matchAny',
    //   // filterList: true,
    //   // highlightMatch: true,
    //   // keyCreator: params => params.value.name,
    //   values: ['English', 'Spanish', 'French', 'Portuguese', '(other)']
    // },

    field: 'group_title',
    tooltip: trackerTooltips.mainGrid.columns.group_title,
    type: 'select',
    title: 'دسته',
    width: 120,
    searchable: true,
    cellRenderer: params =>
      params.value ? (
        <div className="w-100 d-flex h-100 align-items-center justify-content-start gap-2">
          <SubtleBadge
            pill
            bg="info"
            className="d-flex align-items-center gap-1  fs--2"
          >
            {params.value}
          </SubtleBadge>
        </div>
      ) : (
        <ChildrensTooltip
          showOnHover={false}
          placement="top"
          html={trackerTooltips.mainGrid.noGroups}
        >
          <FontAwesomeIcon
            className="cursor-pointer fs--2"
            icon="exclamation-circle"
          />
        </ChildrensTooltip>
      )
  },
  {
    field: 'tags',
    tooltip: trackerTooltips.mainGrid.columns.tags,
    type: 'select',
    title: 'برچسب',
    width: 150,
    searchable: true,
    // editable: true,
    // cellEditor: 'agRichSelectCellEditor',
    // cellEditorParams: {
    //   values: [...tags]?.map(tag => ({ id: tag.value, title: tag.label })),
    //   cellRenderer: params => {
    //     return Array.isArray(params.value) ? (
    //       <div className="w-100 d-flex h-100 align-items-center justify-content-start gap-2">
    //         {params.value.map((v, i) => (
    //           <SubtleBadge
    //             pill
    //             key={i}
    //             bg="info"
    //             className="d-flex align-items-center gap-1  fs--2"
    //           >
    //             {v?.title}
    //           </SubtleBadge>
    //         ))}
    //       </div>
    //     ) : (
    //       params.value && (
    //         <SubtleBadge
    //           pill
    //           bg="info"
    //           className="d-flex align-items-center gap-1  fs--2"
    //         >
    //           {params?.value?.title}
    //         </SubtleBadge>
    //       )
    //     );
    //   },
    //   multiSelect: true,
    //   searchType: 'matchAny',
    //   allowTyping: true,
    //   filterList: true,
    //   highlightMatch: true,
    //   suppressMultiSelectPillRenderer: true,
    //   valueListMaxHeight: 220
    // },
    cellRenderer: params => {
      return Array.isArray(params.value) && params.value.length > 0 ? (
        <div className="w-100 d-flex h-100 align-items-center justify-content-start gap-2"></div>
      ) : (
        <ChildrensTooltip
          showOnHover={false}
          placement="top"
          html={trackerTooltips.mainGrid.noTags}
        >
          <FontAwesomeIcon
            className="cursor-pointer fs--2"
            icon="exclamation-circle"
          />
        </ChildrensTooltip>
      );
    }
  },
  {
    searchable: true,
    field: 'inserted_at',
    tooltip: trackerTooltips.mainGrid.columns.inserted_at,
    type: 'date',
    gp: 'تاریخ',
    title: 'تاریخ درج',
    width: 150,
    cellRenderer: params => <>{timeStampToDate(params.value)?.slice(8)}</>
  },
  {
    searchable: true,
    field: 'updated_at',
    tooltip: trackerTooltips.mainGrid.columns.updated_at,
    type: 'date',
    gp: 'تاریخ',
    title: 'تاریخ به‌روزرسانی',
    width: 150,
    cellRenderer: params => <>{timeStampToDate(params.value)?.slice(8)}</>
  },
  {
    field: 'result_count',
    tooltip: trackerTooltips.mainGrid.columns.result_count,
    type: 'number',
    title: 'تعداد نتایج',
    width: 160,
    searchable: true,
    cellRenderer: params =>
      params.data.device_type !== 1 ? (
        <ChildrensTooltip
          showOnHover={false}
          placement="top"
          html={trackerTooltips.mainGrid.noResOnPhones}
        >
          <FontAwesomeIcon
            className="cursor-pointer fs--2"
            icon="exclamation-circle"
          />
        </ChildrensTooltip>
      ) : (
        numberHumanizer(params.value)
      )
  },
  {
    gp: 'Google Ads',
    field: 'ads_search_volume',
    tooltip: trackerTooltips.mainGrid.columns.ads_avg_search_volume,
    type: 'number',
    title: 'SV',
    width: 100,
    searchable: true,
    cellRenderer: params =>
      !params.data.ads_last_check ? (
        <ChildrensTooltip
          showOnHover={false}
          placement="top"
          html={trackerTooltips.mainGrid.noAds}
        >
          <FontAwesomeIcon
            className="cursor-pointer fs--2"
            icon="exclamation-circle"
          />
        </ChildrensTooltip>
      ) : (
        numberHumanizer(params.value)
      )
  },
  {
    gp: 'Google Ads',
    field: 'ads_competition_index',
    tooltip: trackerTooltips.mainGrid.columns.ads_competition,
    type: 'number',
    title: 'Comp',
    width: 100,
    searchable: true,
    cellRenderer: params =>
      !params.data.ads_last_check ? (
        <ChildrensTooltip
          showOnHover={false}
          placement="top"
          html={trackerTooltips.mainGrid.noAds}
        >
          <FontAwesomeIcon
            className="cursor-pointer fs--2"
            icon="exclamation-circle"
          />
        </ChildrensTooltip>
      ) : (
        numberHumanizer(params.value)
      )
  },

  {
    field: 'length',
    tooltip: trackerTooltips.mainGrid.columns.length,
    type: 'number',
    title: 'طول',
    width: 160,
    searchable: true
  },
  {
    field: 'device_type',
    type: 'select',
    title: 'نوع دستگاه',
    width: 120,
    searchable: true,
    cellRenderer: params => (params.value === 1 ? 'رایانه' : 'گوشی')
  },
  {
    field: 'location',
    type: 'select',
    title: 'مکان جستجو',
    width: 120,
    searchable: true
  }
];
